export const HISTORICAL_COLOR = "#2e7d32";
export const FORECAST_COLOR = "#1565c0";
export const FORECAST_COLOR_PALETTE = ["#1565c0", "#7e57c2", "#00897b", "#ef6c00", "#5c6bc0", "#26a69a"];
export const SCADA_COLOR = "#0f172a";
export const BAND_FILL_COLOR = "#64748b";

export function forecastColor(index) {
  return FORECAST_COLOR_PALETTE[index % FORECAST_COLOR_PALETTE.length];
}

export function forecastSeriesKey(runId) {
  return `forecast_${runId}`;
}

export function runTypeLabel(run) {
  return run?.run_type === "actual_weather" ? "Historical weather" : "Forecast weather";
}

// `timeZone` is the project's configured timezone (e.g. "Europe/Athens"); when
// omitted, falls back to the viewer's browser timezone.
export function formatRunTimestamp(value, timeZone) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    dateStyle: "short",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  });
}

export function formatRunInterval(run) {
  if (!run?.start_date && !run?.end_date) return "";
  if (run.start_date && run.end_date && run.start_date === run.end_date) return run.start_date;
  return `${run.start_date || "?"} → ${run.end_date || "?"}`;
}

export function statusChipColor(status) {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "running" || status === "queued") return "warning";
  return "default";
}

// Trapezoidal integration of a kW series into total MWh. Skips gaps larger than 6h
// (treated as missing data rather than a real interval) so sparse series don't
// silently inflate the total.
export function computeSeriesEnergyMwh(series) {
  const points = (series || [])
    .filter((p) => p?.time && p.generation_kw != null)
    .map((p) => ({ t: new Date(p.time).getTime(), kw: p.generation_kw }))
    .filter((p) => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);
  if (points.length < 2) return null;
  let mwh = 0;
  for (let i = 1; i < points.length; i += 1) {
    const hours = (points[i].t - points[i - 1].t) / 3600000;
    if (hours <= 0 || hours > 6) continue;
    mwh += ((points[i].kw + points[i - 1].kw) / 2) * hours;
  }
  return mwh / 1000;
}

export function seriesPeakKw(series) {
  const values = (series || [])
    .map((p) => p?.generation_kw)
    .filter((v) => v != null && Number.isFinite(v));
  if (!values.length) return null;
  return Math.max(...values);
}

// Largest instantaneous gap between two generation profiles over the interval.
// Aligns the two series by timestamp and returns the signed difference
// (forecast - historical) at the point of maximum absolute divergence, so the
// value carries direction (negative = forecast under actual at that moment).
export function seriesMaxDifferenceKw(forecastSeries, historicalSeries) {
  if (!forecastSeries?.length || !historicalSeries?.length) return null;
  const histByTime = new Map();
  historicalSeries.forEach((p) => {
    const t = p?.time ? new Date(p.time).getTime() : null;
    const kw = p?.generation_kw;
    if (t != null && !Number.isNaN(t) && kw != null && Number.isFinite(kw)) {
      histByTime.set(t, kw);
    }
  });
  let maxDiff = null;
  forecastSeries.forEach((p) => {
    const t = p?.time ? new Date(p.time).getTime() : null;
    const kw = p?.generation_kw;
    if (t == null || Number.isNaN(t) || kw == null || !Number.isFinite(kw)) return;
    const h = histByTime.get(t);
    if (h == null) return;
    const diff = kw - h;
    if (maxDiff == null || Math.abs(diff) > Math.abs(maxDiff)) maxDiff = diff;
  });
  return maxDiff;
}

export function computeAccuracyMetrics(estimateSeries, measuredSeries) {
  if (!estimateSeries?.length || !measuredSeries?.length) return null;
  const measuredByTime = new Map();
  measuredSeries.forEach((p) => {
    const t = p?.time ? new Date(p.time).getTime() : null;
    if (t != null && Number.isFinite(t) && p.generation_kw != null) {
      measuredByTime.set(t, p.generation_kw);
    }
  });
  let sumAbs = 0;
  let sumSq = 0;
  let sumSigned = 0;
  let sumAbsPct = 0;
  let pctCount = 0;
  let n = 0;
  estimateSeries.forEach((p) => {
    const t = p?.time ? new Date(p.time).getTime() : null;
    if (t == null || !Number.isFinite(t) || p?.generation_kw == null) return;
    const measured = measuredByTime.get(t);
    if (measured == null) return;
    const diff = p.generation_kw - measured;
    sumAbs += Math.abs(diff);
    sumSq += diff * diff;
    sumSigned += diff;
    if (measured !== 0) {
      sumAbsPct += Math.abs(diff / measured);
      pctCount += 1;
    }
    n += 1;
  });
  if (n === 0) return null;
  const mape = pctCount > 0 ? (sumAbsPct / pctCount) * 100 : null;
  return {
    n,
    mae: sumAbs / n,
    rmse: Math.sqrt(sumSq / n),
    bias: sumSigned / n,
    mape,
    accuracyPct: mape != null ? Math.max(0, 100 - mape) : null,
  };
}

// Merge historical / N forecast runs / measured series (each `{ time, generation_kw }[]`)
// into rows keyed by timestamp: `{ time, historical, forecast_<runId>..., measured }`.
export function mergeSeriesByTime({ historicalSeries, forecastSeriesList = [], measuredSeries }) {
  const byTime = new Map();
  const addSeries = (series, key) => {
    (series || []).forEach((point) => {
      const t = point?.time ? new Date(point.time).getTime() : null;
      if (t == null || !Number.isFinite(t)) return;
      const row = byTime.get(t) || { time: t };
      row[key] = point.generation_kw;
      byTime.set(t, row);
    });
  };
  addSeries(historicalSeries, "historical");
  forecastSeriesList.forEach(({ runId, series }) => addSeries(series, forecastSeriesKey(runId)));
  addSeries(measuredSeries, "measured");
  return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
}

const GRANULARITY_MS = {
  "15min": null,
  hourly: 3600000,
  daily: 86400000,
};

// Aggregates merged rows to hourly/daily mean values. "15min" is a passthrough
// since the underlying series already comes in ~15 minute steps. Works over
// whatever numeric keys are present (historical/forecast_<id>/measured) so any
// number of forecast run columns are supported.
export function aggregateRows(rows, granularity) {
  const bucketMs = GRANULARITY_MS[granularity];
  if (!bucketMs) return rows;
  const keys = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (k !== "time") keys.add(k);
    });
  });
  const buckets = new Map();
  rows.forEach((row) => {
    const bucketKey = Math.floor(row.time / bucketMs) * bucketMs;
    const bucket = buckets.get(bucketKey) || { time: bucketKey, sums: {}, counts: {} };
    keys.forEach((k) => {
      if (row[k] != null) {
        bucket.sums[k] = (bucket.sums[k] || 0) + row[k];
        bucket.counts[k] = (bucket.counts[k] || 0) + 1;
      }
    });
    buckets.set(bucketKey, bucket);
  });
  return Array.from(buckets.values())
    .sort((a, b) => a.time - b.time)
    .map((bucket) => {
      const out = { time: bucket.time };
      keys.forEach((k) => {
        out[k] = bucket.counts[k] ? bucket.sums[k] / bucket.counts[k] : null;
      });
      return out;
    });
}

// Adds a stacked-area band (`bandBase`/`bandHeight`) spanning the gap between two
// named series keys at each point, used to lightly shade the deviation. Only
// meaningful when comparing exactly one historical run against one forecast run.
export function withDeviationBand(rows, keyA = "historical", keyB = "forecast") {
  return rows.map((row) => {
    if (row[keyA] == null || row[keyB] == null) {
      return { ...row, bandBase: null, bandHeight: null };
    }
    const base = Math.min(row[keyA], row[keyB]);
    const height = Math.abs(row[keyA] - row[keyB]);
    return { ...row, bandBase: base, bandHeight: height };
  });
}

export function formatTooltipValue(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "N/A";
  return `${value.toFixed(1)} kW`;
}

export function formatTooltipDate(value, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const opts = timeZone ? { timeZone } : {};
  const datePart = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", ...opts });
  const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, ...opts });
  return `${datePart}, ${timePart}`;
}

export function formatMwh(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} MWh`;
}

export function formatKw(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} kW`;
}

export function formatPct(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function buildCsv(rows, columns) {
  const header = columns.map((c) => c.label).join(",");
  const lines = rows.map((row) =>
    columns
      .map((c) => {
        const value = c.value(row);
        if (value == null) return "";
        const str = String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      })
      .join(",")
  );
  return [header, ...lines].join("\n");
}

// Serializes a live <svg> element to a PNG and triggers a download. Uses only
// browser-native APIs (XMLSerializer + canvas) so no extra dependency is needed.
export async function exportChartPng(svgElement, filename) {
  if (!svgElement) throw new Error("Chart is not ready to export yet.");
  const bbox = svgElement.getBoundingClientRect();
  const width = Math.max(1, Math.round(bbox.width));
  const height = Math.max(1, Math.round(bbox.height));
  const clone = svgElement.cloneNode(true);
  clone.setAttribute("width", width);
  clone.setAttribute("height", height);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const svgString = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = window.URL.createObjectURL(svgBlob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to render chart image."));
      img.src = svgUrl;
    });
    const canvas = document.createElement("canvas");
    const scale = 2;
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);
    const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    downloadBlob(pngBlob, filename);
  } finally {
    window.URL.revokeObjectURL(svgUrl);
  }
}
